import styled from 'styled-components'
import Text from '../shared/Text'
import styles from '../../utility/styles'
import { formatCurrency, formatTime } from '../../utility/helpers'
import Button from '../shared/Button'
import { useState } from 'react'
import { useCallback } from 'react'
import { useMemo } from 'react'
import { Fragment } from 'react'
import { useMapState } from '../../state/context'

const Wrapper = styled.div``

const Row = styled.div`
	display: flex;
	align-items: center;
	justify-content: space-between;
	margin-bottom: 8px;
`

const CostTypeRow = styled.div`
	display: flex;
	align-items: center;
	justify-content: center;
`

const Separator = styled.div`
	height: 0;
	width: 100%;
	border-top: 1px solid var(--tz-hairline, rgba(255, 255, 255, 0.08));
`

const CostTypeButton = styled(Button)`
	flex: 1;
	background-color: ${styles.colors.background};
	border: ${({ isActive }) =>
		isActive
			? `2px solid ${styles.colors.white}`
			: `1px solid ${styles.colors.lightGray}`};
	font-weight: ${({ isActive }) => (isActive ? '800' : '600')};
	color: ${({ isActive }) =>
		isActive ? styles.colors.white : styles.colors.lightGray};
	margin: 0 8px 12px 8px;
`

/**
 *
 * @param {{
 *   currency_code:string,
 *   currency_symbol_foreign:string,
 *   currency_symbol_local:string,
 *   idle_fees:{unit:string,rates:number[]},
 *   price_list:{rate_unit:string,start:string,end:string,start_end_type:'time'|'power'|'onerate',rate:number}[],
 *   time_of_use_rates:boolean,
 *   type:'tesla_supercharger',
 *   non_tesla_prices:{
 *     idle_fees:{unit:string,rates:number[]},
 *     price_list:{
 *       member:{rate_unit:string,start:string,end:string,start_end_type:'time'|'power'|'onerate',rate:number}[],
 *       non_member:{rate_unit:string,start:string,end:string,start_end_type:'time'|'power'|'onerate',rate:number}[]
 *     },
 *   }
 * }} props
 */
export default function Pricing({
	currency_code,
	currency_symbol_foreign,
	currency_symbol_local,
	idle_fees,
	price_list = [],
	time_of_use_rates,
	type,
	non_tesla_prices,
}) {
	const [{ showNonTeslaOnly }] = useMapState()
	const [activePricing, setActivePricing] = useState(() => showNonTeslaOnly ? 'nonTesla' : 'tesla')
	const setPricingTesla = useCallback(
		() => setActivePricing('tesla'),
		[setActivePricing]
	)
	const setPricingNonTesla = useCallback(
		() => setActivePricing('nonTesla'),
		[setActivePricing]
	)
	const nonPriceList = useMemo(() => {
		const member = non_tesla_prices?.price_list?.member ?? []
		const nonMember = non_tesla_prices?.price_list?.non_member ?? []
		const merged = member.map((thing, i) => ({
			...thing,
			rateNm: nonMember[i]?.rate,
		}))
		return merged
	}, [non_tesla_prices])
	return (
		<Wrapper>
			{Boolean(non_tesla_prices) && (
				<CostTypeRow>
					<CostTypeButton
						onClick={setPricingTesla}
						isActive={activePricing === 'tesla'}
					>
						Tesla Pricing
					</CostTypeButton>
					<CostTypeButton
						onClick={setPricingNonTesla}
						isActive={activePricing === 'nonTesla'}
					>
						Non Tesla Rates
					</CostTypeButton>
				</CostTypeRow>
			)}
			{activePricing === 'tesla' &&
				price_list.map(
					({ rate, rate_unit, start, end, start_end_type }) => (
						<Row key={`price-${rate}`}>
							<Text.Caption>
								{start_end_type === 'onerate'
									? 'Cost'
									: `${formatTime(start)} - ${formatTime(
											end
									  )}`}
							</Text.Caption>
							<Text.Caption>
								{!!currency_code &&
									formatCurrency(rate, currency_code)}
								/{rate_unit}
							</Text.Caption>
						</Row>
					)
				)}
			{activePricing === 'nonTesla' &&
				nonPriceList?.map(
					({
						rate,
						rate_unit,
						start,
						end,
						start_end_type,
						rateNm,
					}) => (
						<Row style={{ flex: 1 }} key={`price-${rate}`}>
							<Row style={{ flex: 1, marginBottom: '0' }}>
								<Text.Caption>
									{start_end_type === 'onerate'
										? 'Cost'
										: `${formatTime(start)} - ${formatTime(
												end
										  )}`}
								</Text.Caption>
								<Text.Caption>
									{!!currency_code &&
										formatCurrency(rate, currency_code)}
									/{rate_unit}
								</Text.Caption>
							</Row>
							<Text.Caption
								style={{ marginLeft: '32px' }}
								color={styles.colors.lightGray}
							>
								{!!currency_code &&
									formatCurrency(rateNm, currency_code)}
								/{rate_unit}*
							</Text.Caption>
						</Row>
					)
				)}
			{activePricing === 'nonTesla' && (
				<Row style={{ justifyContent: 'flex-end' }}>
					<Text.Tiny color={styles.colors.lightGray}>
						*Non-member pricing
					</Text.Tiny>
				</Row>
			)}
			<Separator />
		</Wrapper>
	)
}
